import React, { FC, useEffect, useState } from 'react'
import { Button, Well, Drawer } from '@pushly/aqe/lib/components'
import { EditOutlined } from '@ant-design/icons'
import { Form, Input, Skeleton, Typography } from 'antd'
import { useService } from '@pushly/aqe/lib/hooks'
import { useForm } from 'antd/es/form/Form'
import * as randomstring from 'randomstring'
import { AppService, AiGenerationDirectiveService } from '../../services'
import { getClassNames } from '../../_utils/classnames'
import { simpleFormErrorNotification, simpleNotification } from '../../_utils/utils'
import { DEFAULT_DRAWER_FORM_ITEM_LAYOUT } from '../../constants'
import { AiGenerationDirective } from '@pushly/models/lib/structs/pushly-ai/ai-generation-directive'
import { CreateAiGenerationDirectiveRequest } from '@pushly/models/lib/structs/pushly-ai/create-ai-generation-directive-request'
import { UpdateAiGenerationDirectiveRequest } from '@pushly/models/lib/structs/pushly-ai/update-ai-generation-directive-request'

interface IBrandVoiceEditor {
    domainId: number
    directive: AiGenerationDirective | null
    visible: boolean
    onClose: (e: any) => void
    onSaved: (directive: AiGenerationDirective) => void
}

const BrandVoiceEditor: FC<IBrandVoiceEditor> = (props) => {
    const appSvc = useService(AppService)
    const directiveSvc = useService(AiGenerationDirectiveService)

    const [form] = useForm()
    const [isDirty, setIsDirty] = useState(false)

    const initialBody = props.directive?.active_version?.body ?? ''

    const handleSubmit = async () => {
        try {
            const { body } = await form.validateFields()

            const res = props.directive
                ? await directiveSvc.update(
                      props.domainId,
                      props.directive.id,
                      UpdateAiGenerationDirectiveRequest.from({ body }),
                  )
                : await directiveSvc.create(
                      props.domainId,
                      CreateAiGenerationDirectiveRequest.from({ role: 'brand_voice', name: 'Brand Voice', body }),
                  )

            if (res.ok && res.data) {
                simpleNotification('success', 'Brand Voice was successfully updated')
                props.onSaved(res.data)
            }
        } catch (err) {
            simpleFormErrorNotification(err)
        }
    }

    return (
        <Drawer
            getContainer={appSvc.getAppContainer}
            className={getClassNames('ddf-brand-voice-editor')}
            title="Update Brand Voice"
            placement="right"
            closable={true}
            onSubmit={handleSubmit}
            disableSubmit={!isDirty}
            onClose={props.onClose}
            open={props.visible}
            size={800}
        >
            <Form
                form={form}
                initialValues={{ body: initialBody }}
                onValuesChange={() => setIsDirty(true)}
                {...DEFAULT_DRAWER_FORM_ITEM_LAYOUT}
                requiredMark={false}
                size="small"
                labelAlign="left"
                layout="vertical"
            >
                <Form.Item
                    name="body"
                    label="Brand Voice"
                    rules={[{ required: true, whitespace: true, message: 'Brand Voice cannot be empty' }]}
                    extra="Free-text guidance applied to all AI-generated content for this domain."
                >
                    <Input.TextArea
                        rows={12}
                        autoSize={{ minRows: 8, maxRows: 20 }}
                        placeholder="e.g. Calm, no-hype voice. Speak plainly and avoid exclamation marks. Always mention the markets."
                    />
                </Form.Item>
            </Form>
        </Drawer>
    )
}

interface IBrandVoiceWell {
    domainId: number
    canEdit: boolean
}

export const BrandVoiceWell: FC<IBrandVoiceWell> = (props) => {
    const directiveSvc = useService(AiGenerationDirectiveService)

    const [loading, setLoading] = useState(true)
    const [directive, setDirective] = useState<AiGenerationDirective | null>(null)
    const [showDrawer, setShowDrawer] = useState(false)
    const [drawerKey, setDrawerKey] = useState<string>('')

    const loadBrandVoice = async () => {
        setLoading(true)
        const res = await directiveSvc.fetchBrandVoice(props.domainId)
        if (res.ok) {
            setDirective(res.data ?? null)
        }
        setLoading(false)
    }

    useEffect(() => {
        loadBrandVoice()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.domainId])

    const body = directive?.active_version?.body

    return (
        <Well
            className="nested"
            title="Brand Voice"
            actions={
                <Button
                    size="small"
                    onClick={() => {
                        setDrawerKey(randomstring.generate())
                        setShowDrawer(true)
                    }}
                    disabled={!props.canEdit || loading}
                >
                    <EditOutlined />
                    <span>Edit</span>
                </Button>
            }
            footerMode="hidden"
        >
            <Skeleton loading={loading} active={true} title={false}>
                {!loading &&
                    (body ? (
                        <Typography.Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>
                            {body}
                        </Typography.Paragraph>
                    ) : (
                        <Typography.Text type="secondary">
                            No Brand Voice set. AI-generated content will use default behavior.
                        </Typography.Text>
                    ))}
            </Skeleton>

            <BrandVoiceEditor
                key={drawerKey}
                domainId={props.domainId}
                directive={directive}
                visible={showDrawer}
                onClose={() => setShowDrawer(false)}
                onSaved={(updated) => {
                    setDirective(updated)
                    setShowDrawer(false)
                }}
            />
        </Well>
    )
}
